'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MembershipRegistrationModalProps {
  tierName: string;
  tierId: string;
  tierPrice: string;
  onSubmit: (data: {
    fullName: string;
    email: string;
    phone?: string;
    tierId: string;
    tierName: string;
    tierPrice: string;
    message?: string;
  }) => void;
  onCancel: () => void;
}

const MembershipRegistrationModal: React.FC<MembershipRegistrationModalProps> = ({
  tierName,
  tierId,
  tierPrice,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone || undefined,
        tierId,
        tierName,
        tierPrice,
        message: formData.message || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6 overflow-y-auto">
      <div className="bg-card rounded-lg max-w-2xl w-full p-8 relative">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-muted/80 transition-colors"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={24} className="text-foreground" />
        </button>

        <div className="mb-6">
          <h2 className="text-3xl font-headline font-bold text-foreground mb-2">
            Join as {tierName}
          </h2>
          <p className="text-muted-foreground">
            Complete your registration to become a member of the global peace movement.
          </p>
        </div>

        <div className="mb-6 p-4 bg-primary/10 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-foreground font-medium">Membership Tier:</span>
            <span className="text-primary font-bold">{tierName}</span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-foreground font-medium">Price:</span>
            <span className="text-primary font-bold">
              {tierPrice === 'Free' ? 'Free' : `${tierPrice}/month`}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
              Full Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              required
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email Address <span className="text-destructive">*</span>
            </label>
            <input
              type="email"
              id="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
              Message (Optional)
            </label>
            <textarea
              id="message"
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Tell us why you want to join the movement..."
            />
          </div>

          <div className="flex items-center space-x-4 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Complete Registration'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-muted text-foreground rounded-md font-medium transition-all duration-300 hover:bg-muted/80"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MembershipRegistrationModal;